import React, {useEffect, useRef, useState} from "react";
import {motion} from "framer-motion";
import {ChevronRight, Menu} from "lucide-react";
import {Button} from "@/components/ui/button";
import JobSearchForm from "../components/forms/JobSearchForm";
import PricingSection from "../components/landing/PricingSection";
import BenefitsSection from "../components/landing/BenefitsSection";
import FAQSection from "../components/landing/FAQSection";
import JobResultsManager from '@/components/job-results/JobResultsManager';
import Navbar from "@/components/Navbar";


export default function Landing() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const [selectedPackage, setSelectedPackage] = useState("free");

  // Click outside to close menu
  useEffect(() => {
    function handleClickOutside(event) {
      if (mobileMenuOpen &&
          menuRef.current &&
          !menuRef.current.contains(event.target) &&
          menuButtonRef.current &&
          !menuButtonRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-[#0F172A] backdrop-blur-md shadow-md fixed top-0 left-0 right-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
            <div className="flex items-center">
              <span className="text-xl font-bold text-white"><a href="#" onClick={() => window.scrollTo({
                top: 0,
                behavior: 'smooth'
              })}>JobPool<span className="text-blue-400">.ai</span></a></span>
            </div>

            <div className="hidden md:flex items-center space-x-1">

              <Navbar />

              <a href="#pricing"
                 onClick={(e) => {
                   e.preventDefault();
                   const element = document.getElementById('pricing');
                   if (element) element.scrollIntoView({behavior: 'smooth'});
                 }}
                 className="px-3 py-2 text-sm font-medium text-gray-200 hover:text-indigo-400 transition-colors">
                Pricing
              </a>
              <a href="#benefits"
                 onClick={(e) => {
                   e.preventDefault();
                   const element = document.getElementById('benefits');
                   if (element) element.scrollIntoView({behavior: 'smooth'});
                 }}
                 className="px-3 py-2 text-sm font-medium text-gray-200 hover:text-indigo-400 transition-colors">
                Benefits
              </a>
              <a href="#faq"
                 onClick={(e) => {
                   e.preventDefault();
                   const element = document.getElementById('faq');
                   if (element) element.scrollIntoView({behavior: 'smooth'});
                 }}
                 className="px-3 py-2 text-sm font-medium text-gray-200 hover:text-indigo-400 transition-colors">
                FAQ
              </a>
              <a
                  href="https://discord.gg/3KCaxfTgNY"
                  className="px-3 py-2 text-sm font-medium text-gray-200 hover:text-indigo-400 transition-colors"
              >
                Discord
              </a>
            </div>

            <Button
                ref={menuButtonRef}
                variant="ghost"
                size="icon"
                className="md:hidden text-white hover:bg-[#1E293B]"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="w-6 h-6"/>
            </Button>
          </div>

          {mobileMenuOpen && (
              <div className="md:hidden">
                <div className="absolute right-0 top-16 w-56 z-[999] bg-[#0F172A] shadow-lg py-3 rounded-bl-lg border border-gray-800"
                     ref={menuRef}>
                  <nav className="flex flex-col">
                    <div className="px-6 py-2 flex justify-center">
                      <Navbar isInMobileMenu={true} />
                    </div>
                    <div className="border-t border-gray-800 my-2"></div>
                    <a
                        href="#pricing"
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById('pricing');
                          if (element) {
                            element.scrollIntoView({behavior: 'smooth'});
                            setTimeout(() => setMobileMenuOpen(false), 10);
                          }
                        }}
                        className="px-6 py-3 text-gray-200 hover:bg-[#1E293B] transition-colors"
                    >
                      Pricing
                    </a>
                    <a
                        href="#benefits"
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById('benefits');
                          if (element) {
                            element.scrollIntoView({behavior: 'smooth'});
                            setTimeout(() => setMobileMenuOpen(false), 10);
                          }
                        }}
                        className="px-6 py-3 text-gray-200 hover:bg-[#1E293B] transition-colors"
                    >
                      Benefits
                    </a>
                    <a
                        href="#faq"
                        onClick={(e) => {
                          e.preventDefault();
                          const element = document.getElementById('faq');
                          if (element) {
                            element.scrollIntoView({behavior: 'smooth'});
                            setTimeout(() => setMobileMenuOpen(false), 10);
                          }
                        }}
                        className="px-6 py-3 text-gray-200 hover:bg-[#1E293B] transition-colors"
                    >
                      FAQ
                    </a>
                    <a
                        href="https://discord.gg/3KCaxfTgNY"
                        className="px-6 py-3 text-gray-200 hover:bg-[#1E293B] transition-colors"
                    >
                      Discord
                    </a>
                  </nav>
                </div>
              </div>
          )}
        </header>

        <main className="pt-16">
          {/* HERO SECTION - Main container for the highlighted blocks in red */}
          {/*
            CUSTOMIZATION TIP:
            - To adjust the overall padding/height of this hero section, modify these classes:
              * Vertical padding: Change "pt-4 md:pt-10 pb-8 md:pb-16" to increase/decrease space
              * Adjust the container height with custom classes if needed (e.g., min-h-[600px])
            - To change the gradient background colors, modify the "from-blue-600 to-indigo-700" classes
          */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 relative overflow-hidden" id="hero-section">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
              <div
                  className="absolute top-10 left-10 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-blob"></div>
              <div
                  className="absolute top-0 right-0 w-72 h-72 bg-blue-300 rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
              <div
                  className="absolute -bottom-8 left-20 w-72 h-72 bg-indigo-400 rounded-full mix-blend-overlay filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
            </div>

            {/*
              CUSTOMIZATION TIP:
              - This container controls the width and horizontal padding of the entire hero section
              - Change "max-w-7xl" to adjust the maximum width
              - Modify "px-4 sm:px-6" to change horizontal padding
            */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
              {/*
                CUSTOMIZATION TIP - Main grid layout:
                - This grid controls the layout of the left content and right search form
                - To adjust spacing between columns, change "gap-4 md:gap-8"
                - To change column proportions, adjust "md:grid-cols-12" and "md:col-span-5"/"md:col-span-7"
                - For vertical spacing, adjust "pt-4 md:pt-10 pb-8 md:pb-16"
              */}
              <div className="pt-8 md:pt-14 pb-16 md:pb-24 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start">
                {/* LEFT COLUMN - Marketing Text Block ID: marketing-content */}
                {/*
                  CUSTOMIZATION TIP - Left column spacing:
                  - Adjust vertical margins with "mb-10 md:mb-20" and "mt-6 md:mt-20 lg:mt-24"
                  - Change column width with "md:col-span-5" (increase number for wider column)
                  - Add "pr-0 md:pr-4" to control right padding specifically
                */}
                <div className="col-span-1 md:col-span-5 mb-2 md:mb-6 pb-2 md:pb-4 mt-0 md:mt-2 lg:mt-4" id="marketing-content">
                  {/* BLOCK 1: Headline Content ID: headline-block */}
                  {/*
                    CUSTOMIZATION TIP - Headline:
                    - Adjust font size with "text-4xl md:text-5xl"
                    - Change spacing between lines by adding "leading-tight" or "leading-relaxed"
                    - Add "mb-2" to control space between the two text lines
                  */}
                  <motion.h1
                      initial={{opacity: 0, y: 20}}
                      animate={{opacity: 1, y: 0}}
                      transition={{duration: 0.5}}
                      className="text-4xl md:text-5xl font-bold"
                      id="headline-block"
                  >
                    <span className="text-white drop-shadow-sm">Stop Searching.</span>
                    <br/>
                    <span className="text-blue-200 drop-shadow-sm">Start Matching.</span>
                  </motion.h1>

                  {/* BLOCK 2: Subtext Content ID: subtext-block */}
                  {/*
                    CUSTOMIZATION TIP - Subtext:
                    - Control spacing from headline with "mt-8"
                    - Adjust font size/weight with "text-lg" and "font-medium"
                    - Control line height with "leading-relaxed"
                    - Change width by adding "max-w-md" or similar
                  */}
                  <motion.p
                      initial={{opacity: 0, y: 20}}
                      animate={{opacity: 1, y: 0}}
                      transition={{duration: 0.5, delay: 0.1}}
                      className="mt-6 text-lg text-white/90 leading-relaxed font-medium"
                      id="subtext-block"
                  >
                    Our AI analyzes your resume and preferences to find your perfect job matches. Get connected with
                    opportunities that truly fit your skills and aspirations.
                  </motion.p>

                  {/* BLOCK 3: Stats Grid Block ID: stats-grid */}
                  {/*
                    CUSTOMIZATION TIP - Stats Grid:
                    - Adjust vertical spacing from text with "mt-10 md:mt-8"
                    - Control gap between stat boxes with "gap-4"
                    - Change grid layout with "grid-cols-3" (use grid-cols-2 for 2 columns, etc.)
                    - For individual stat box styling, modify the classes inside the map function
                  */}
                  <motion.div
                      initial={{opacity: 0, y: 20}}
                      animate={{opacity: 1, y: 0}}
                      transition={{duration: 0.5, delay: 0.2}}
                      className="mt-10 md:mt-8"
                      id="stats-grid"
                  >
                    <div className="grid grid-cols-3 gap-4 w-full">
                      {[
                        {value: "99%", label: "Match Accuracy", badge: "up to"},
                        {value: "30k+", label: "Jobs Analyzed"},
                        {value: "3x", label: "Faster Interviews"}
                      ].map((stat, index) => (
                          <div key={index}
                               className="bg-white/10 backdrop-blur-sm rounded-xl p-4 shadow-lg border border-white/20 transition-transform hover:scale-105 hover:bg-white/15 relative">
                            {stat.badge && (
                              <span className="absolute top-1 right-2 bg-blue-500/40 text-[9px] px-1.5 py-0.5 rounded-sm text-white font-medium">
                                {stat.badge}
                              </span>
                            )}
                            <p className="text-2xl md:text-3xl font-bold text-white">{stat.value}</p>
                            <p className="text-sm text-blue-100 mt-1 font-medium">{stat.label}</p>
                          </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* BLOCK 4: Video Demo Block ID: video-demo */}
                  {/*
                    CUSTOMIZATION TIP - Video Section:
                    - Control vertical spacing with "mt-6 md:mt-10"
                    - Adjust image size with "w-[390px] h-[250px]" (can use other values)
                    - Use "max-w-md" to constrain overall width
                  */}
                  <div className="mt-6 md:mt-10 hidden md:block" id="video-demo">
                    {/*<h3 className="text-xl font-semibold mb-3 text-white">How JobPool.ai Works - and How to Use It</h3>*/}
                    <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 shadow-lg w-full max-w-3xl mx-auto">
                      <h2 className="text-2xl font-bold text-white text-center mb-6">Choose Your Plan</h2>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm text-white text-center">
                          <thead>
                          <tr className="text-xs uppercase bg-white/10 text-white">
                            <th className="py-3 px-4">Package</th>
                            <th className="py-3 px-4">Price</th>
                            <th className="py-3 px-4">Main Benefit</th>
                            <th className="py-3 px-4">Short Description</th>
                          </tr>
                          </thead>
                          <tbody className="divide-y divide-white/20">
                          <tr>
                            <td className="py-4 px-4 font-semibold">Free</td>
                            <td className="py-4 px-4 font-bold">$0</td>
                            <td className="py-4 px-4">5 Jobs</td>
                            <td className="py-4 px-4">Try our AI matching for free</td>
                          </tr>
                          <tr>
                            <td className="py-4 px-4 font-semibold">Basic</td>
                            <td className="py-4 px-4 font-bold">$9.99</td>
                            <td className="py-4 px-4">10 Jobs</td>
                            <td className="py-4 px-4">Higher Accuracy<br/>More Interviews</td>
                          </tr>
                          <tr>
                            <td className="py-4 px-4 font-semibold">Premium</td>
                            <td className="py-4 px-4 font-bold">$19.99</td>
                            <td className="py-4 px-4">20 Jobs</td>
                            <td className="py-4 px-4">Higher Accuracy<br/>More Interviews</td>
                          </tr>
                          <tr>
                            <td className="py-4 px-4 font-semibold">Elite</td>
                            <td className="py-4 px-4 font-bold">$29.99</td>
                            <td className="py-4 px-4">30 Jobs</td>
                            <td className="py-4 px-4">Higher Accuracy<br/>More Interviews</td>
                          </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RIGHT COLUMN - Search Form Block ID: search-form-container */}
                {/*
                  CUSTOMIZATION TIP - Right Column:
                  - Adjust column width with "md:col-span-7" (increase/decrease for width)
                  - Add custom padding with "pl-0 md:pl-4" for left padding
                  - For vertical alignment, change "items-start" in the parent grid to "items-center"
                */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                    className="md:pl-10 col-span-1 md:col-span-7"
                    id="search-form-container"
                >
                  {/*
                    CUSTOMIZATION TIP - Search Form Card:
                    - Control card styling with "bg-white shadow-xl rounded-2xl border border-blue-100"
                    - Adjust inner padding with "p-5 md:p-6 lg:p-8"
                    - Add custom width/height constraints with classes like "max-w-2xl" or "min-h-[500px]"
                    - For responsive behavior, use different padding values for mobile/desktop
                  */}
                  <div className="bg-white shadow-xl rounded-2xl border border-blue-100">
                    <div className="p-5 md:p-6 lg:p-8">
                      <JobSearchForm
                          selectedPackage={selectedPackage}
                          onPackageChange={setSelectedPackage}
                      />
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>


          <JobResultsManager />


          <div id="pricing" className="bg-[#eef5ff]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <PricingSection
                  selectedPackage={selectedPackage}
                  onPackageSelect={setSelectedPackage}
              />
            </div>
          </div>

          <div id="benefits" className="bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <BenefitsSection/>
            </div>
          </div>

          <div id="faq" className="bg-blue-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <FAQSection/>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-20 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Ready to Upgrade Your Job Search?</h2>
              <p className="text-blue-100 text-lg mb-8 max-w-3xl mx-auto tracking-normal">
                Save days of searching and land your dream job with JobPool.ai's cutting-edge technology!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                    href="#pricing"
                    onClick={(e) => {
                      e.preventDefault();
                      const element = document.getElementById('pricing');
                      if (element) element.scrollIntoView({behavior: 'smooth'});
                    }}
                    className="inline-flex items-center justify-center bg-white text-blue-600 font-medium py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
                >
                  View Pricing
                  <ChevronRight className="ml-2 w-5 h-5"/>
                </a>
                <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      window.scrollTo({top: 0, behavior: "smooth"});
                    }}
                    className="inline-flex items-center justify-center bg-blue-600 text-white font-medium py-3 px-6 rounded-full shadow-lg hover:bg-blue-700 transition-all transform hover:-translate-y-1"
                >
                  Start Now
                </a>
              </div>
            </div>
          </div>
        </main>

        <style jsx global>{`
          /* CUSTOMIZATION TIP - Animation Keyframes:
           * You can adjust the animation speed by changing the animation duration
           * Current setting: "animation: blob 7s infinite"
           * For faster animations, reduce the time (e.g., 5s or 3s)
           * For slower animations, increase the time
           */
          @keyframes blob {
            0% {
              transform: translate(0px, 0px) scale(1);
            }
            33% {
              transform: translate(30px, -50px) scale(1.1);
            }
            66% {
              transform: translate(-20px, 20px) scale(0.9);
            }
            100% {
              transform: translate(0px, 0px) scale(1);
            }
          }

          .animate-blob {
            animation: blob 7s infinite;
          }

          .animation-delay-2000 {
            animation-delay: 2s;
          }

          .animation-delay-4000 {
            animation-delay: 4s;
          }

          @keyframes shine {
            to {
              background-position: 200% center;
            }
          }

          .shine-effect {
            background: linear-gradient(90deg, #ffffff 0%, #f0f4ff 50%, #ffffff 100%);
            background-size: 200% auto;
            animation: shine 3s linear infinite;
          }

          textarea {
            resize: none !important;
          }

          /* CUSTOMIZATION TIP - Responsive Adjustments:
           * Add custom breakpoints for more precise control:
           * 
           * For example:
           * @media (min-width: 640px) and (max-width: 1023px) {
           *   .custom-tablet-spacing {
           *     margin-top: 2rem;
           *     padding: 1.5rem;
           *   }
           * }
           *
           * @media (min-width: 1024px) {
           *   .custom-desktop-spacing {
           *     margin-top: 3rem;
           *     padding: 2rem;
           *   }
           * }
           */
        `}</style>
      </div>
  );
}

// Based on your request, I'll add comprehensive comments to the Landing.jsx file to help you control the spacing, dimensions, and make the red-boxed areas more customizable and responsive. I'll also add labels to make it easier to find and customize these blocks.
//
//     I've enhanced the Landing.jsx file with comprehensive comments and added ID labels to the blocks you marked in red. Here's what I've done:
//
// ### 🏷️ Labels Added to Red Blocks
//
// 1. **Hero Section Container**: `id="hero-section"` - The entire blue gradient area
// 2. **Marketing Content**: `id="marketing-content"` - The left column containing headline, subtext, stats
// 3. **Headline Block**: `id="headline-block"` - "Stop Searching. Start Matching." text
// 4. **Subtext Block**: `id="subtext-block"` - The paragraph below the headline
// 5. **Stats Grid**: `id="stats-grid"` - The 3-column metrics section (99%, 30k+, 3x)
// 6. **Video Demo**: `id="video-demo"` - The "How JobPool.ai Works" section
// 7. **Search Form Container**: `id="search-form-container"` - The right column with the white search form
//
// ### 📏 Spacing & Dimension Controls
//
// I've added detailed comments explaining how to:
//
// - **Adjust vertical spacing** between blocks (using `mt-` and `mb-` classes)
// - **Control horizontal spacing** (using `px-`, `py-`, and `gap-` classes)
// - **Modify width and height** of elements (using Tailwind width/height classes)
// - **Change column proportions** (by adjusting the grid column values)
//
// ### 📱 Desktop vs Mobile Customization
//
// The comments include specific guidance for:
//
// - **Responsive breakpoints** (using `md:` and other prefixes)
// - **Mobile-specific adjustments** (like different paddings for small screens)
// - **Desktop-specific styling** (for larger displays)
// - **Hiding/showing elements** based on screen size (like the video section)
//
// ### 🎨 Additional Customization Tips
//
// I've also included comments about:
//
// - **Background color controls** (gradient modifications)
// - **Animation speed adjustments** (in the CSS section)
// - **Border and shadow styling**
// - **Font size and weight controls**
//
// You can now easily find any section using the IDs, and the detailed comments provide guidance on how to customize spacing, size, and responsiveness according to your preferences.